import ActivityDefinition from '@/models/activityDefinition';
import ActivityDefinitionState from './activityDefinitionsState';
import ActivityEvent from '@/models/activityEvent';
import { AxiosResponse, AxiosError } from 'axios';
import { RootState } from '@/store/rootState';
import Vue from 'vue';
import VueApp, { bus } from '@/main';
import Vuex, { ActionContext, Module } from 'vuex';

Vue.use(Vuex);

export const activityDefinitionStore: Module<ActivityDefinitionState, RootState> = {
  namespaced: true,
  state: new ActivityDefinitionState(),
  mutations: {
    clearJobActivityDefinitions(state: ActivityDefinitionState): void {
      Vue.set(state, 'jobActivityDefinitions', []);
    },

    updateActivityDefinition(state: ActivityDefinitionState, ad: ActivityDefinition): void {
      if (ad !== undefined) {
        const index = state.activityDefinitions?.indexOf(ad) as number;
        if (index > -1) {
          Vue.set(state.activityDefinitions as ActivityDefinition[], index, ad);
        }
      }
    },

    updateActivityDefinitions(state: ActivityDefinitionState, definitions: ActivityDefinition[]): void {
      Vue.set(state, 'activityDefinitions', definitions);
    },

    updateJobActivityDefinitions(
      state: ActivityDefinitionState,
      args: { definitions: ActivityDefinition[]; preserveEvents: boolean },
    ): void {
      if (!state.jobActivityDefinitions) {
        Vue.set(state, 'jobActivityDefinitions', []);
      }
      for (let i = 0; i < args.definitions.length; i++) {
        const updatedDef = state.jobActivityDefinitions?.find((jad) => jad.id === args.definitions[i].id);
        if (updatedDef) {
          if (args.preserveEvents) args.definitions[i].events = updatedDef.events;
          Object.assign(updatedDef, args.definitions[i]);
        } else if (state.jobActivityDefinitions) {
          state.jobActivityDefinitions.push(args.definitions[i]);
        }
      }
    },

    updateJobActivityDefinitionEvents(state: ActivityDefinitionState, events: ActivityEvent[]): void {
      if (!state.jobActivityDefinitions) {
        Vue.set(state, 'jobActivityDefinitions', []);
      }
      for (let i = 0; i < events.length; i++) {
        const eventDef = state.jobActivityDefinitions?.find((jad) => jad.id === events[i].activityDefinitionId);
        if (!eventDef) continue;
        const event = eventDef.events.find((e) => e.id === events[i].id);
        if (event) {
          Object.assign(event, events[i]);
        } else {
          const sortDir = localStorage.getItem('model-pipeline-activity-event-sort-direction') || 'asc';
          if (sortDir === 'asc') eventDef.events.push(events[i]);
          if (sortDir === 'desc') eventDef.events.unshift(events[i]);
        }
      }
    },
  },

  actions: {
    async clearJobActivityDefinitions({ commit }: ActionContext<ActivityDefinitionState, RootState>): Promise<void> {
      commit('clearJobActivityDefinitions');
    },

    async getActivityDefinitions(
      { commit }: ActionContext<ActivityDefinitionState, RootState>,
      args: { activityDefinitionId?: string; jobId?: string; preserveEvents?: boolean },
      callback?: () => void,
    ): Promise<void> {
      if (!VueApp) return;
      const params = args?.jobId ? ['jobId=' + args.jobId] : [];
      if (args?.activityDefinitionId) params.push('activityDefinitionId=' + args.activityDefinitionId);
      const url = 'activitydefinition' + (params.length > 0 ? '?' + params.join('&') : '');
      VueApp.$http
        .get<never>(url)
        .then((result: AxiosResponse<ActivityDefinition[]>) => {
          const defs = [] as ActivityDefinition[];
          result.data.forEach((ad) => defs.push(new ActivityDefinition(ad)));
          if (args?.jobId) {
            commit('updateJobActivityDefinitions', { definitions: defs, preserveEvents: args.preserveEvents });
          } else {
            commit('updateActivityDefinitions', defs);
          }
          if (callback) callback();
        })
        .catch((error: AxiosError) => {
          bus.$emit('alert', 'Get activity definitions failed ', 'error', 6000, error);
        });
    },

    async commitActivityDefinition(
      { commit }: ActionContext<ActivityDefinitionState, RootState>,
      ad: ActivityDefinition,
    ): Promise<void> {
      commit('updateActivityDefinition', ad);
    },

    async commitJobActivityDefinitions(
      { commit }: ActionContext<ActivityDefinitionState, RootState>,
      activityDefinitions: ActivityDefinition[],
    ): Promise<void> {
      commit('updateJobActivityDefinitions', { definitions: activityDefinitions });
    },

    async commitJobActivityDefinitionEvents(
      { commit }: ActionContext<ActivityDefinitionState, RootState>,
      activityEvents: ActivityEvent[],
    ): Promise<void> {
      commit('updateJobActivityDefinitionEvents', activityEvents);
    },
  },

  getters: {
    getPhaseDefinitions:
      (state: ActivityDefinitionState) =>
      (phaseType: number): ActivityDefinition[] => {
        return state.jobActivityDefinitions?.filter((def) => def.pipelinePhaseTypeId === phaseType) ?? [];
      },
  },
};
